import React, { useState, useEffect } from "react";
import { useQuery, useLazyQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";

import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import BlockIcon from "@material-ui/icons/Block";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import EditIcon from "@material-ui/icons/Edit";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import UserDetails from "./UserDetails";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import DateRangeFilter from "designV2/components/DateRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import TableToolbar from "designV2/components/TableToolbar";
import Button from "designV2/components/Button";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import { endPoint } from "utils/config";
import { Avatar, Stars, readableError } from "./peopleFields";
import "./superPeople.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const USER_FILTERS = `
  _id: $_id
  fullName: $fullName
  email: $email
  phoneNumber: $phoneNumber
  gender: $gender
  state: $state
  createdAtFrom: $createdAtFrom
  createdAt: $createdAt
`;

const USER_VARIABLES = `
  $limit: Int
  $skip: Int
  $_id: ID
  $fullName: String
  $email: String
  $phoneNumber: String
  $gender: UserGender
  $state: UserStateInput
  $createdAtFrom: Date
  $createdAt: Date
  $createdAtSort: Int
  $birthDateSort: Int
`;

const GET_USERS = gql`
  query getUsersByAdmin(${USER_VARIABLES}) {
    getUsersByAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: { ${USER_FILTERS} }
      sort: { createdAt: $createdAtSort, birthDate: $birthDateSort }
    ) {
      _id
      fullName
      email
      phoneNumber
      gender
      state
      birthDate
      isVerified
      emailVerified
      phoneNumberVerified
      hasNotification
      stripeCustomerId
      averageRate
      numberOfRates
      createdAt
      updatedAt
      defaultAddress {
        full
        zipCode
      }
      addresses {
        full
        zipCode
      }
    }
    getUsersByAdminCount(filters: { ${USER_FILTERS} })
  }
`;

const GET_USER_COUNTS = gql`
  query getCustomerCounts {
    total: getUsersByAdminCount(filters: {})
    active: getUsersByAdminCount(filters: { state: ACTIVE })
    suspended: getUsersByAdminCount(filters: { state: SUSPENDED })
  }
`;

const SUSPEND_USERS = gql`
  mutation suspendUserByAdmin($idSet: [ID!]!) {
    suspendUserByAdmin(idSet: $idSet) {
      _id
      state
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const STATE_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "SUSPENDED", label: "Suspended" },
];
const STATE_LABELS = STATE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const GENDER_OPTIONS = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
  { value: "OTHER", label: "Other" },
];
const GENDER_LABELS = GENDER_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SEARCH_TARGETS = [
  { value: "fullName", label: "Name" },
  { value: "phoneNumber", label: "Phone" },
  { value: "email", label: "Email" },
];
const SEARCH_LABELS = SEARCH_TARGETS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "youngest", label: "Youngest first" },
  { value: "oldestAge", label: "Oldest first (age)" },
];

const FILTER_DEBOUNCE_MS = 350;

const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "");

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const Users = () => {
  const [searchTarget, setSearchTarget] = useState("fullName");
  const [searchTerm, setSearchTerm] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOption, setSortOption] = useState("newest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [selectedIds, setSelectedIds] = useState([]);
  const [userDetail, setUserDetail] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState("");
  const [exporting, setExporting] = useState(false);

  const [variables, setVariables] = useState({ limit: perPage, skip: 0, createdAtSort: -1 });

  useEffect(() => {
    const handler = setTimeout(() => {
      const term = searchTerm.trim();
      setVariables((v) => ({
        ...v,
        skip: 0,
        fullName: undefined,
        phoneNumber: undefined,
        email: undefined,
        ...(term ? { [searchTarget]: term } : {}),
        state: stateFilter || undefined,
        gender: genderFilter || undefined,
        createdAtFrom: dateFrom || undefined,
        createdAt: dateTo || undefined,
        createdAtSort:
          sortOption === "oldest" ? 1 : sortOption === "newest" ? -1 : undefined,
        // birthDate ascending is the oldest person, so the labels are
        // inverted relative to the sort direction.
        birthDateSort:
          sortOption === "youngest" ? -1 : sortOption === "oldestAge" ? 1 : undefined,
      }));
      setPageCount(0);
      setSelectedIds([]);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [searchTarget, searchTerm, stateFilter, genderFilter, dateFrom, dateTo, sortOption]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
    setSelectedIds([]);
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_USERS, {
    variables,
    fetchPolicy: "network-only",
  });
  const { data: countsData, refetch: refetchCounts } = useQuery(GET_USER_COUNTS, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });
  const [fetchAllForExport, { data: exportData }] = useLazyQuery(GET_USERS, {
    fetchPolicy: "network-only",
  });
  const [suspendUsers] = useMutation(SUSPEND_USERS);

  const allUsers = (data && data.getUsersByAdmin) || [];
  const serverCount = (data && data.getUsersByAdminCount) || 0;
  const counts = countsData || {};

  // Verification is three separate booleans with no server-side filter,
  // so this one narrows the page in the browser and says so.
  const users = verifiedFilter
    ? allUsers.filter((user) => {
        const verified = user.emailVerified && user.phoneNumberVerified;
        return verifiedFilter === "verified" ? verified : !verified;
      })
    : allUsers;

  const unverifiedOnPage = allUsers.filter(
    (user) => !user.emailVerified || !user.phoneNumberVerified
  ).length;

  /* ---------------- selection + suspend ---------------- */

  const toggleSelectOne = (id) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : current.concat(id)
    );

  const toggleSelectAll = () => {
    const ids = users.map((u) => u._id);
    setSelectedIds((current) => (current.length === ids.length ? [] : ids));
  };

  const checkboxState =
    users.length > 0 && selectedIds.length === users.length
      ? "checked"
      : selectedIds.length > 0
      ? "indeterminate"
      : "unchecked";

  const runSuspend = () => {
    const targets = suspendTarget || [];
    setWorking(true);
    setActionError("");
    suspendUsers({ variables: { idSet: targets.map((u) => u._id) } })
      .then(() => {
        setWorking(false);
        setSuspendTarget(null);
        setUserDetail(null);
        setSelectedIds([]);
        refetch();
        refetchCounts();
      })
      .catch((err) => {
        setWorking(false);
        setSuspendTarget(null);
        setActionError(readableError(err));
      });
  };

  /* ---------------- filter chips ---------------- */

  const trimmedTerm = searchTerm.trim();
  const activeFilterChips = [];
  if (trimmedTerm) {
    activeFilterChips.push({ id: "search", label: `${SEARCH_LABELS[searchTarget]}: ${trimmedTerm}` });
  }
  if (stateFilter) activeFilterChips.push({ id: "state", label: `State: ${STATE_LABELS[stateFilter]}` });
  if (genderFilter) {
    activeFilterChips.push({ id: "gender", label: `Gender: ${GENDER_LABELS[genderFilter]}` });
  }
  if (verifiedFilter) {
    activeFilterChips.push({
      id: "verified",
      label: verifiedFilter === "verified" ? "Fully verified" : "Not fully verified",
    });
  }
  if (dateFrom || dateTo) {
    activeFilterChips.push({ id: "date", label: `Joined: ${dateFrom || "-"} to ${dateTo || "-"}` });
  }

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "state") setStateFilter("");
    if (id === "gender") setGenderFilter("");
    if (id === "verified") setVerifiedFilter("");
    if (id === "date") {
      setDateFrom("");
      setDateTo("");
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setStateFilter("");
    setGenderFilter("");
    setVerifiedFilter("");
    setDateFrom("");
    setDateTo("");
  };

  /* ---------------- export ---------------- */

  useEffect(() => {
    if (!exporting || !exportData) return;
    const rows = [
      ["Name", "Phone", "Email", "Gender", "State", "Email verified", "Phone verified", "Rating", "Joined"],
    ];
    (exportData.getUsersByAdmin || []).forEach((u) => {
      rows.push([
        u.fullName || "-",
        u.phoneNumber || "-",
        u.email || "-",
        GENDER_LABELS[u.gender] || u.gender || "-",
        STATE_LABELS[u.state] || u.state || "-",
        u.emailVerified ? "yes" : "no",
        u.phoneNumberVerified ? "yes" : "no",
        u.averageRate ? Number(u.averageRate).toFixed(1) : "-",
        moment(u.createdAt).format("MM/DD/YYYY"),
      ]);
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), "Customers");
    XLSX.writeFile(wb, "Customers.xlsx");
    setExporting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const handleExport = () => {
    if (serverCount === 0 || exporting) return;
    setExporting(true);
    fetchAllForExport({ variables: { ...variables, skip: 0, limit: serverCount } });
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page superPeoplePage superCustomersPage">
        <main className="main">
          <PageHeader title="Customers" description="Everyone with a Spark account." />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={7} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  const selectedUsers = users.filter((u) => selectedIds.includes(u._id));

  return (
    <div className="page superPeoplePage superCustomersPage">
      <main className="main">
        <PageHeader
          title="Customers"
          count={serverCount}
          description="Everyone with a Spark account, how to reach them, and whether they are verified."
          actions={
            <PageActionButton
              icon={<CloudDownloadIcon fontSize="small" />}
              label={exporting ? "Exporting" : "Export"}
              info={exporting ? "Preparing file" : `${serverCount} customer${serverCount === 1 ? "" : "s"}`}
              onClick={handleExport}
              disabled={serverCount === 0}
              loading={exporting}
            />
          }
        />

        <section className="stats">
          <MetricCard icon={<PeopleOutlineIcon />} title="Matching customers" value={serverCount} />
          <MetricCard
            icon={<VerifiedUserIcon />}
            title="Active"
            value={counts.active || 0}
            tone="success"
          />
          <MetricCard
            icon={<BlockIcon />}
            title="Suspended"
            value={counts.suspended || 0}
            tone={counts.suspended > 0 ? "danger" : "default"}
          />
          <MetricCard
            icon={<MailOutlineIcon />}
            title="Not fully verified"
            value={unverifiedOnPage}
            hint="On this page"
            tone={unverifiedOnPage > 0 ? "warning" : "default"}
          />
        </section>

        <section className="filters">
          <SearchField
            placeholder={`Search by ${SEARCH_LABELS[searchTarget].toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={`Search in: ${SEARCH_LABELS[searchTarget]}`}
              options={SEARCH_TARGETS}
              value={searchTarget}
              active={false}
              onChange={(e) => setSearchTarget(e.target.value)}
            />
            <FilterDropdown
              label={stateFilter ? `State: ${STATE_LABELS[stateFilter]}` : "State"}
              options={[{ value: "", label: "Any state" }, ...STATE_OPTIONS]}
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            />
            <FilterDropdown
              label={genderFilter ? `Gender: ${GENDER_LABELS[genderFilter]}` : "Gender"}
              options={[{ value: "", label: "Any gender" }, ...GENDER_OPTIONS]}
              value={genderFilter}
              onChange={(e) => setGenderFilter(e.target.value)}
            />
            <FilterDropdown
              label={
                verifiedFilter
                  ? verifiedFilter === "verified"
                    ? "Fully verified"
                    : "Not fully verified"
                  : "Verification"
              }
              options={[
                { value: "", label: "Any verification" },
                { value: "verified", label: "Fully verified" },
                { value: "unverified", label: "Not fully verified" },
              ]}
              value={verifiedFilter}
              onChange={(e) => setVerifiedFilter(e.target.value)}
            />
            <DateRangeFilter
              label="Joined"
              fromValue={dateFrom}
              toValue={dateTo}
              onFromChange={(e) => setDateFrom(e.target.value)}
              onToChange={(e) => setDateTo(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        <TableToolbar
          selectedCount={selectedIds.length}
          checkboxState={checkboxState}
          onCheckboxChange={toggleSelectAll}
          bulkActions={
            <Button
              variant="dangerQuiet"
              size="sm"
              icon={<PauseCircleOutlineIcon fontSize="small" />}
              onClick={() => setSuspendTarget(selectedUsers)}
            >
              Suspend selected
            </Button>
          }
          leftContent={
            verifiedFilter ? (
              <span className="superPersonPill">
                {users.length} of {allUsers.length} on this page
              </span>
            ) : null
          }
          rightActions={
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => {
                refetch();
                refetchCounts();
              }}
              title="Refresh customers"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        />

        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="superPeopleSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell tableHeaderCheckbox">
                <Checkbox
                  size="small"
                  checked={checkboxState === "checked"}
                  indeterminate={checkboxState === "indeterminate"}
                  onChange={toggleSelectAll}
                  inputProps={{ "aria-label": "Select every customer on this page" }}
                />
              </div>
              <div className="tableHeaderCell">Customer</div>
              <div className="tableHeaderCell">Contact</div>
              <div className="tableHeaderCell">Verification</div>
              <div className="tableHeaderCell">Rating</div>
              <div className="tableHeaderCell">Joined</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {users.length === 0 ? (
              <EmptyState
                icon={<PeopleOutlineIcon />}
                title={activeFilterChips.length === 0 ? "No customers yet" : "No matches"}
                message={
                  activeFilterChips.length === 0
                    ? "Customers appear here once people sign up in the app."
                    : "No customers match these filters. Try widening or clearing them."
                }
              />
            ) : (
              users.map((user) => (
                <div className="row" key={user._id}>
                  <Checkbox
                    size="small"
                    checked={selectedIds.includes(user._id)}
                    onChange={() => toggleSelectOne(user._id)}
                    inputProps={{ "aria-label": `Select ${user.fullName}` }}
                  />

                  <div className="superPersonCell" data-label="Customer">
                    <Avatar name={user.fullName} />
                    <span className="superPersonText">
                      <span className="superPersonName">{user.fullName || "Unnamed"}</span>
                      <span className="superPersonMeta">
                        {[GENDER_LABELS[user.gender], user.birthDate ? formatDate(user.birthDate) : null]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                  </div>

                  <div className="superPersonText" data-label="Contact">
                    <span className="superPersonName">{user.phoneNumber || "No phone"}</span>
                    <span className="superPersonMeta">{user.email || "No email"}</span>
                  </div>

                  <span className="superPersonRow" data-label="Verification">
                    <StatusBadge
                      status={user.phoneNumberVerified ? "success" : "warning"}
                      label="Phone"
                      dot={false}
                    />
                    <StatusBadge
                      status={user.emailVerified ? "success" : "warning"}
                      label="Email"
                      dot={false}
                    />
                  </span>

                  <span data-label="Rating">
                    <Stars value={user.averageRate} />
                  </span>

                  <span className="updated" data-label="Joined">
                    <strong>{formatDate(user.createdAt)}</strong>
                    <span>
                      {user.state === "SUSPENDED" ? "Suspended" : `${user.numberOfRates || 0} ratings`}
                    </span>
                  </span>

                  <div className="superPersonActions">
                    <IconButton
                      size="small"
                      className="rowActionButton"
                      onClick={() =>
                        (window.location.href = `${endPoint}/superAdmin/editUser/${user._id}`)
                      }
                      title="Edit customer"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      className="rowActionButton"
                      onClick={() => setUserDetail(user)}
                      title="Customer details"
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              ))
            )}
          </section>
        </div>

        {serverCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={serverCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell
        open={Boolean(userDetail)}
        onClose={() => setUserDetail(null)}
        title="Customer details"
        subtitle={userDetail ? userDetail.fullName : ""}
        size="lg"
      >
        {userDetail && (
          <React.Fragment>
            <UserDetails user={userDetail} />
            <div className="superPersonFooter">
              {userDetail.state !== "SUSPENDED" && (
                <Button variant="dangerQuiet" onClick={() => setSuspendTarget([userDetail])}>
                  Suspend
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={() =>
                  (window.location.href = `${endPoint}/superAdmin/editUser/${userDetail._id}`)
                }
              >
                Edit
              </Button>
            </div>
          </React.Fragment>
        )}
      </DialogShell>

      <DialogShell
        open={Boolean(suspendTarget)}
        onClose={() => (working ? null : setSuspendTarget(null))}
        title={suspendTarget && suspendTarget.length > 1 ? "Suspend customers" : "Suspend customer"}
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setSuspendTarget(null), disabled: working },
          { label: "Suspend", onClick: runSuspend, variant: "danger", loading: working },
        ]}
      >
        <div className="superPersonConfirm">
          {suspendTarget && suspendTarget.length > 1
            ? `${suspendTarget.length} customers will lose access to the app and cannot place orders or book trips.`
            : "This customer will lose access to the app and cannot place orders or book trips."}
        </div>
      </DialogShell>
    </div>
  );
};

export default Users;
